/**
 * Mentor Creative 點數(購點 / 回跳入點 / 退款 / 發票)型別 + mapper
 * -----------------------------------------------------------------------------
 * 計劃書 = 「Mentor Creative 點數重構計劃書」§6(Creative FE 工作包)/ §7(v1.0.1 欄位定稿)。
 * wire = **Creative BE docs/141 v2.1**(`/api/wallet/mentor/*`;BE 主導,2026-11-02 定案)。
 * 錢、發票、付款頁在 Mentor;點數帳(per-order FIFO)與訂單在 Creative BE;FE 只跟 Creative BE
 * 講話,Mentor 只出現在一個地方:按「前往付款」後整頁導去的 `pay_url`。
 *
 * 三條口徑(docs/141 開頭 + 計劃書 §7):
 *   1. **使用者看到的金額一律含稅**:含稅值都由 BE 算好(`display_amount` / `*_tax_incl`),
 *      FE **不自己 ×1.05**;未稅只出現在明細「Mentor 平台費儲值」那一列。
 *      ⚠️ 退款那幾個欄(試算 `amount` / `fee` / `net_amount`、Refund `amount` / `refunded_amount`)
 *      依 docs/141 §3 是**含稅**(公式就是拿含稅實付算的),不套「`*_amount` = 未稅」那條通則。
 *   2. 退款金額 = floor(剩餘點數 × 含稅實付 ÷ 總點數〔含贈送〕),**由 BE 算**(`refund-quote`)。
 *      FE 只顯示公式;{@link refundAmountByFormula} 只給 fixture 與測試對照用。
 *   3. 一單只能**成功**退一次(失敗不算):按鈕出不出現只看 BE 的 `can_refund`。
 *
 * wire 值域是開放的(R296):`display_status` / Refund `status` / `payment_method` 都以 `string` 收,
 * 呈現端對認不得的值顯**原碼**,不畫成某個已知狀態。
 */

// ─── 套餐 ────────────────────────────────────────────────────────────────────

export interface PointsPackageDto {
  code: string;
  /** 未稅。 */
  paid_amount: number;
  /** 含稅(Mentor 算好的顯示價)。 */
  display_amount: number;
  /** 總點數(含贈送)。 */
  points: number;
  paid_points?: number | null;
  bonus_points?: number | null;
}

export interface PointsPackage {
  code: string;
  priceExclTax: number;
  priceInclTax: number;
  /** 總點數(含贈送)。 */
  points: number;
  /** 其中贈送的點數(沒有 = 0)。 */
  bonusPoints: number;
}

export function mapPointsPackageDto(dto: PointsPackageDto): PointsPackage {
  return {
    code: dto.code,
    priceExclTax: dto.paid_amount,
    priceInclTax: dto.display_amount,
    points: dto.points,
    bonusPoints: dto.bonus_points ?? 0,
  };
}

// ─── 環境狀態(M2)─────────────────────────────────────────────────────────

export interface PointsStatusDto {
  configured: boolean;
  gid?: string | null;
  purchased_points?: number | null;
  held_for_refund?: number | null;
}

export interface PointsStatus {
  /** false = 本環境沒接 Mentor(藏入口)。 */
  configured: boolean;
}

// ─── 退款 ────────────────────────────────────────────────────────────────────

/** 退款狀態三相(BE 有多個中間態;FE 只分三種講話方式)。 */
export type PointsRefundPhase = 'processing' | 'settled' | 'failed' | 'unknown';

/**
 * docs/141 §3:`HOLDING` / `SUBMITTED` / `SETTLING` / `RELEASING` = 處理中;`SETTLED` = 已退款;
 * `FAILED` = 失敗(點數已還回,可用新 refund_id 再申請)。`PROCESSING` 是 Mentor 端的叫法(計劃書 §7 G4),
 * 一併當處理中。認不得 → `unknown`(畫面顯原碼)。
 */
export function refundPhaseOf(status: string): PointsRefundPhase {
  switch (status) {
    case 'HOLDING':
    case 'SUBMITTED':
    case 'SETTLING':
    case 'RELEASING':
    case 'PROCESSING':
      return 'processing';
    case 'SETTLED':
      return 'settled';
    case 'FAILED':
      return 'failed';
    default:
      return 'unknown';
  }
}

export interface PointsRefundDto {
  refund_id: string;
  order_ref?: string | null;
  status: string;
  points?: number | null;
  /** 依公式算出的退款金額(含稅)。 */
  amount?: number | null;
  /** 手續費(含稅)。 */
  fee?: number | null;
  /** 含稅實退(退款完成後才有)。 */
  refunded_amount?: number | null;
  failure_code?: string | null;
  created_at?: string | null;
}

export interface PointsRefund {
  refundId: string;
  status: string;
  points: number | null;
  amount: number | null;
  fee: number | null;
  refundedAmount: number | null;
  failureCode: string | null;
  createdAt: string | null;
}

export function mapPointsRefundDto(dto: PointsRefundDto): PointsRefund {
  return {
    refundId: dto.refund_id,
    status: dto.status,
    points: dto.points ?? null,
    amount: dto.amount ?? null,
    fee: dto.fee ?? null,
    refundedAmount: dto.refunded_amount ?? null,
    failureCode: dto.failure_code ?? null,
    createdAt: dto.created_at ?? null,
  };
}

// ─── 訂單 ────────────────────────────────────────────────────────────────────

/** 已知的顯示狀態(`display_status`;wire 值域開放,認不得的照原碼顯示)。 */
export const POINTS_ORDER_STATUSES = [
  'PENDING_PAYMENT',
  'PAID',
  'REFUNDING',
  'REFUNDED',
  'CANCELLED',
  'EXPIRED',
] as const;
export type KnownPointsOrderStatus = (typeof POINTS_ORDER_STATUSES)[number];

export function isKnownPointsOrderStatus(s: string): s is KnownPointsOrderStatus {
  return (POINTS_ORDER_STATUSES as readonly string[]).includes(s);
}

export interface PointsOrderDto {
  order_ref: string;
  package_code: string;
  /** BE 原始狀態(INITIATED / PENDING_PAYMENT / CREDITED / …);畫面不用。 */
  status?: string | null;
  /** 膠囊用這個。 */
  display_status: string;
  can_refund?: boolean | null;
  can_continue_payment?: boolean | null;
  pay_url?: string | null;
  /** 建單時套餐快照(付款前顯示)。 */
  quoted_paid_amount?: number | null;
  quoted_display_amount?: number | null;
  quoted_points?: number | null;
  /** 入點後(Mentor 為準)。 */
  paid_amount?: number | null;
  paid_amount_tax_incl?: number | null;
  paid_points?: number | null;
  bonus_points?: number | null;
  points_total?: number | null;
  /** 本單還剩幾點(**含**退款圈存中);未入點 = null。 */
  points_remaining?: number | null;
  points_held_for_refund?: number | null;
  points_used?: number | null;
  refund?: PointsRefundDto | null;
  /** `CARD` / `ATM`;還沒在 Mentor 付款頁選 = null。 */
  payment_method?: string | null;
  business_name?: string | null;
  credited_at?: string | null;
  created_at?: string | null;
}

export interface PointsOrder {
  orderRef: string;
  packageCode: string;
  /** 顯示狀態(`display_status`)。 */
  status: string;
  createdAt: string | null;
  paidAt: string | null;
  pointsTotal: number;
  bonusPoints: number;
  pointsUsed: number;
  /** 還能用的點數(= `points_remaining − points_held_for_refund`)。 */
  pointsRemaining: number;
  /** 退款圈存中。 */
  pointsRefunding: number;
  priceExclTax: number;
  /** 營業稅 = 含稅 − 未稅(兩個數字都是 BE 給的,不是 FE 算稅)。 */
  taxAmount: number;
  priceInclTax: number;
  paymentMethod: string | null;
  businessName: string | null;
  refund: PointsRefund | null;
  canRefund: boolean;
  canContinuePayment: boolean;
  /** 建單回應才有(待付款單);繼續付款一律重取,不用這個。 */
  payUrl: string | null;
}

/**
 * BE 沒給 `can_refund` 時的保守推算:已付款、還有剩、沒有進行中或已成功的退款。
 * ⚠️ 只在**缺欄**時用;BE 給了就以 BE 為準 —— 「一單只能成功退一次」的真相在 BE 與 Mentor,
 * FE 推錯的方向必須是「少給按鈕」。
 */
export function deriveCanRefund(o: Pick<PointsOrder, 'status' | 'pointsRemaining' | 'refund'>): boolean {
  if (o.status !== 'PAID') return false;
  if (o.pointsRemaining <= 0) return false;
  if (o.refund) {
    const phase = refundPhaseOf(o.refund.status);
    if (phase !== 'failed') return false;
  }
  return true;
}

function upperOrNull(v: string | null | undefined): string | null {
  return typeof v === 'string' && v !== '' ? v.toUpperCase() : null;
}

export function mapPointsOrderDto(dto: PointsOrderDto): PointsOrder {
  const refund = dto.refund ? mapPointsRefundDto(dto.refund) : null;
  const held = dto.points_held_for_refund ?? 0;
  const priceExclTax = dto.paid_amount ?? dto.quoted_paid_amount ?? 0;
  const priceInclTax = dto.paid_amount_tax_incl ?? dto.quoted_display_amount ?? 0;
  const status = dto.display_status;
  const base = {
    orderRef: dto.order_ref,
    packageCode: dto.package_code,
    status,
    createdAt: dto.created_at ?? null,
    paidAt: dto.credited_at ?? null,
    pointsTotal: dto.points_total ?? dto.quoted_points ?? 0,
    bonusPoints: dto.bonus_points ?? 0,
    pointsUsed: dto.points_used ?? 0,
    pointsRemaining: Math.max(0, (dto.points_remaining ?? 0) - held),
    pointsRefunding: held,
    priceExclTax,
    taxAmount: priceInclTax - priceExclTax,
    priceInclTax,
    paymentMethod: upperOrNull(dto.payment_method),
    businessName: dto.business_name ?? null,
    refund,
    payUrl: dto.pay_url ?? null,
  };
  return {
    ...base,
    canRefund: typeof dto.can_refund === 'boolean' ? dto.can_refund : deriveCanRefund(base),
    canContinuePayment:
      typeof dto.can_continue_payment === 'boolean' ? dto.can_continue_payment : status === 'PENDING_PAYMENT',
  };
}

export interface PointsOrderPageDto {
  result: PointsOrderDto[];
  next_cursor?: string | null;
  summary?: {
    points_remaining?: number | null;
    points_refunding?: number | null;
  } | null;
}

export interface PointsSummary {
  /** 購買點數還剩多少(目前工作區所有訂單加總,BE 算);拿不到 = null(畫面顯「—」,不補 0)。 */
  pointsRemaining: number | null;
  pointsRefunding: number | null;
}

export interface PointsOrderPage {
  items: PointsOrder[];
  nextCursor: string | null;
  summary: PointsSummary;
}

export function mapPointsOrderPageDto(dto: PointsOrderPageDto): PointsOrderPage {
  return {
    items: (dto.result ?? []).map(mapPointsOrderDto),
    nextCursor: dto.next_cursor ?? null,
    summary: {
      pointsRemaining: dto.summary?.points_remaining ?? null,
      pointsRefunding: dto.summary?.points_refunding ?? null,
    },
  };
}

// ─── 退款試算 ────────────────────────────────────────────────────────────────

export interface PointsRefundQuoteDto {
  order_ref?: string;
  points_remaining: number;
  points_total: number;
  paid_amount_tax_incl: number;
  amount: number;
  fee?: number | null;
  net_amount?: number | null;
  /** `CARD`(退回原信用卡)/ `BANK`(ATM 單,銀行自動退回原轉帳帳戶)/ null(付款方式未知)。 */
  refund_method?: string | null;
}

export interface PointsRefundQuote {
  pointsRemaining: number;
  pointsTotal: number;
  priceInclTax: number;
  amount: number;
  /** null = 暫時查不到 Mentor 手續費 → 畫面顯「手續費另計」,不顯 0。 */
  fee: number | null;
  /** 試算(max(0, amount − fee));實退以退款完成的 `refunded_amount` 為準。 */
  netAmount: number | null;
  refundMethod: string | null;
}

export function mapPointsRefundQuoteDto(dto: PointsRefundQuoteDto): PointsRefundQuote {
  return {
    pointsRemaining: dto.points_remaining,
    pointsTotal: dto.points_total,
    priceInclTax: dto.paid_amount_tax_incl,
    amount: dto.amount,
    fee: dto.fee ?? null,
    netAmount: dto.net_amount ?? null,
    refundMethod: upperOrNull(dto.refund_method),
  };
}

/**
 * 退款公式(計劃書 §7):floor(剩餘點數 × 含稅實付 ÷ 總點數〔含贈送〕)。
 * 例:付 100 買 100 送 10 → 沒用全退 100;用 20 剩 90 → 退 81。
 *
 * ⚠️ 畫面上的金額一律用 BE 的 `refund-quote.amount`,**不拿這支取代**:這支只給 fixture
 * 與測試對照(真相在 BE,而 Mentor 還會再驗 ≦ 實付 − 已退 − 在途)。
 */
export function refundAmountByFormula(pointsRemaining: number, priceInclTax: number, pointsTotal: number): number {
  if (pointsTotal <= 0 || pointsRemaining <= 0) return 0;
  return Math.floor((pointsRemaining * priceInclTax) / pointsTotal);
}

// ─── 電子發票(M7)─────────────────────────────────────────────────────────

/**
 * **資訊為主、連結為輔**:號碼 / 日期 / 時間 / 隨機碼 / 狀態是鯨躍原樣字串;`url` 可能沒有
 * (公開載具站渲染延遲)。`url` 新分頁開、不要存(無身分驗證、會隨作廢 / 折讓變)。
 */
export interface PointsInvoiceDto {
  url?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_time?: string | null;
  random_number?: string | null;
  invoice_status?: string | null;
}

export interface PointsInvoice {
  url: string | null;
  number: string | null;
  date: string | null;
  time: string | null;
  randomNumber: string | null;
  /** 鯨躍原樣(「開立」/「作廢」/「全部折讓」…,開放值域);缺欄 = null。 */
  status: string | null;
}

function strOrNull(v: string | null | undefined): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

export function mapPointsInvoiceDto(dto: PointsInvoiceDto | null | undefined): PointsInvoice {
  return {
    url: strOrNull(dto?.url),
    number: strOrNull(dto?.invoice_number),
    date: strOrNull(dto?.invoice_date),
    time: strOrNull(dto?.invoice_time),
    randomNumber: strOrNull(dto?.random_number),
    status: strOrNull(dto?.invoice_status),
  };
}
